import { Card, CardContent } from "@/components/ui/card"
import { CreditReportData, CreditReportType } from "@prescreen/lib/types/credit-report-client"
import { apiFetch } from "@prescreen/lib/api-client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { EqDates, EquifaxPrequalReport } from "@prescreen/lib/equifax-prequal-report"
import { DitDecision } from "@prescreen/lib/equifax-dit"

interface CreditReportsTabProps {
  prescreenRequestIdFilter?: string
}

export default function CreditReportsTab({ prescreenRequestIdFilter }: CreditReportsTabProps) {
  const [creditReports, setCreditReports] = useState<CreditReportData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const getReportTimestamp = (report: CreditReportData): Date => {
    try {
      // For DIT reports, timestamp is in report.timestamp
      if (report.reportType === CreditReportType.Dit && report.report?.timestamp) {
        return new Date(report.report.timestamp)
      }
      // For prequal reports, check report.timestamp
      const reportDate = report.report?.consumers?.equifaxUSConsumerCreditReport?.[0]?.reportDate
      if (reportDate) {
        return EqDates.toDate(reportDate)
      }
      // Fallback: if _id is an ObjectId, extract timestamp from it
      // Otherwise, use current date as fallback
      return new Date()
    } catch {
      return new Date()
    }
  }

  const fetchCreditReports = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const path = prescreenRequestIdFilter
        ? `/api/credit-reports?prescreenRequestId=${encodeURIComponent(prescreenRequestIdFilter)}`
        : '/api/credit-reports'
      const response = await apiFetch(path)
      
      if (!response.ok) {
        throw new Error('Failed to fetch credit reports')
      }
      
      const result = await response.json()
      
      if (result.success && result.data?.creditReports) {
        // Sort by timestamp, most recent first
        const sorted = [...result.data.creditReports].sort((a, b) => {
          const timestampA = getReportTimestamp(a)
          const timestampB = getReportTimestamp(b)
          return timestampB.getTime() - timestampA.getTime()
        })
        setCreditReports(sorted)
      } else {
        throw new Error(result.error || 'Failed to load credit reports')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCreditReports()
  }, [prescreenRequestIdFilter])

  const getConsumerName = (report: CreditReportData): string => {
    try {
      if (report.reportType === CreditReportType.Dit) {
        // For DIT reports, get name from request
        if (report.request && 'data' in report.request) {
          const ditRequest = report.request as any
          const name = ditRequest.data?.identity?.name
          if (name) {
            const parts = [
              name.firstName,
              name.middleName,
              name.lastName
            ].filter(Boolean)
            return parts.join(' ') || 'Unknown'
          }
        }
        return 'Unknown'
      } else if (report.reportType === CreditReportType.Prequal || 
          report.reportType === CreditReportType.PrequalCombined ||
          report.reportType === CreditReportType.PrequalCoborrower) {
        const subjectName = report.report?.consumers?.equifaxUSConsumerCreditReport?.[0]?.subjectName
        if (subjectName) {
          const parts = [
            subjectName.firstName,
            subjectName.middleName,
            subjectName.lastName
          ].filter(Boolean)
          return parts.join(' ') || 'Unknown'
        }
      }
      return 'Unknown'
    } catch {
      return 'Unknown'
    }
  }

  const getDitDecision = (report: CreditReportData): DitDecision | string => {
    // Use ditDecision from API if available, otherwise fallback to report.decision
    if (report.ditDecision) {
      return report.ditDecision
    }

    try {
      if (report.reportType === CreditReportType.Dit) {
        return (report.report?.decision as DitDecision) || 'Error'
      }
      return '' // Not applicable for prequal reports
    } catch {
      return 'Error'
    }
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleReportClick = (reportId: string | undefined) => {
    if (reportId) {
      navigate(`/credit-reports/${reportId}`)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-gray-500">
              Loading credit reports...
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              Error: {error}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (creditReports.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-gray-500">
              No credit reports found.
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">
          Credit Reports ({creditReports.length})
        </h2>
        <button
          onClick={fetchCreditReports}
          className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
        >
          Refresh
        </button>
      </div>

      <div className="grid gap-4">
        {creditReports.map((report) => {
          const timestamp = getReportTimestamp(report)
          const prequalResult = report.prequalResult
          const ditDecision = getDitDecision(report)
          const isDit = report.reportType === CreditReportType.Dit
          const isPrequal = report.reportType === CreditReportType.Prequal || 
                           report.reportType === CreditReportType.PrequalCombined ||
                           report.reportType === CreditReportType.PrequalCoborrower

          return (
            <Card 
              key={report._id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleReportClick(report._id)}
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getConsumerName(report)}
                      </h3>
                      <Badge variant="outline">
                        {report.reportType}
                      </Badge>
                      <Badge variant="secondary">
                        {report.source}
                      </Badge>
                      {isPrequal && prequalResult && prequalResult !== 'Unknown' && prequalResult !== '' && (
                        <Badge 
                          variant={
                            prequalResult === 'pass' ? 'default' : 
                            prequalResult === 'no-hit' || prequalResult === 'no-hit-final' || prequalResult === 'pending' ? 'secondary' : 
                            'destructive'
                          }
                        >
                          {prequalResult}
                        </Badge>
                      )}
                      {isDit && ditDecision && (
                        <Badge 
                          variant={
                            ditDecision === 'Approve' ? 'default' : 
                            ditDecision === 'Deny' ? 'destructive' : 
                            'secondary'
                          }
                        >
                          {ditDecision}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatDate(timestamp)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

